import { CalendarIcon, Star, StarOff, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomerSearchMultiSelect } from '@/components/shared/customer-search-multi-select';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { CreatePromiseFormCustomer } from '@/hooks/useCreatePromiseData';
import { useCreditControllersList } from '@/hooks/useCreditControllersData';
import { useCurrenciesList } from '@/hooks/useCurrenciesData';
import { useCustomersListGraphQL } from '@/hooks/useCustomersGraphQL';
import { filtersMatch } from '@/hooks/useFilterPreferences';
import { isRangeInvalid, parseRangeInput, RANGE_MIN_GREATER_THAN_MAX_MESSAGE } from '@/lib/range-filter-validation';
import { cn, formatDate, parseLocalDate, toLocalDateString } from '@/lib/utils';
import type { ReceiptFilters } from '@/types/payments';
import type { SavedFilterPreset } from '@/types/transaction';

const allocationStatuses = [
  { value: 'unallocated', label: 'Unallocated' },
  { value: 'partially_allocated', label: 'Partially Allocated' },
  { value: 'fully_allocated', label: 'Fully Allocated' },
];

interface ReceiptFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: ReceiptFilters;
  onApplyFilters: (filters: ReceiptFilters) => void;
  savedPresets: SavedFilterPreset<ReceiptFilters>[];
  activePresetId: string | null;
  onApplyPreset: (preset: SavedFilterPreset<ReceiptFilters>) => void;
  onDeletePreset: (presetId: string) => void;
  onSavePreset: (draftFilters: ReceiptFilters) => void;
  onClearAll: () => void;
}

function createCustomerFallback(id: number): CreatePromiseFormCustomer {
  return {
    id: String(id),
    name: `Customer ${id}`,
    accountNumber: '',
    email: '',
    phone: '',
  };
}

export function ReceiptFilterSheet({
  open,
  onOpenChange,
  filters,
  onApplyFilters,
  savedPresets,
  activePresetId,
  onApplyPreset,
  onDeletePreset,
  onSavePreset,
  onClearAll,
}: Readonly<ReceiptFilterSheetProps>) {
  const [draftFilters, setDraftFilters] = useState<ReceiptFilters>({ ...filters });
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);
  const draftPresetRef = useRef<SavedFilterPreset<ReceiptFilters> | null>(null);

  // Sync draft with applied filters when sheet opens
  useEffect(() => {
    if (open) {
      setDraftFilters({ ...filters });
      draftPresetRef.current = null;
    }
  }, [open, filters]);

  // Track selected customer objects for the multi-select (keeps labels in sync)
  const [selectedCustomers, setSelectedCustomers] = useState<CreatePromiseFormCustomer[]>([]);
  const { data: selectedCustomerData } = useCustomersListGraphQL(
    {
      customerIds: draftFilters.customerIds,
      pageSize: Math.max(draftFilters.customerIds.length, 20),
    },
    { enabled: open && draftFilters.customerIds.length > 0 },
  );

  // Sync selected customers when the sheet opens with pre-existing filter IDs
  // (customers already selected from a previous session are kept as-is)
  useEffect(() => {
    if (open) {
      setSelectedCustomers((previousCustomers) => {
        const customersById = new Map(previousCustomers.map((x) => [x.id, x]));
        for (const customer of selectedCustomerData?.customers ?? []) {
          customersById.set(customer.id, {
            id: customer.id,
            name: customer.name,
            accountNumber: customer.accountNumber,
            email: customer.contact,
            phone: customer.phone,
            creditControllerId: customer.creditControllerId,
          });
        }

        return draftFilters.customerIds.map((id) => customersById.get(String(id)) ?? createCustomerFallback(id));
      });
    }
  }, [open, draftFilters.customerIds, selectedCustomerData]);

  const handleCustomerSelectionChange = useCallback((customers: CreatePromiseFormCustomer[]) => {
    setSelectedCustomers(customers);
    setDraftFilters((prev) => ({
      ...prev,
      customerIds: customers.map((c) => parseInt(c.id, 10)),
    }));
  }, []);

  // Mode-aware data for dropdowns
  const { data: currencies } = useCurrenciesList();
  const { data: creditControllers } = useCreditControllersList({ enabled: open });

  const currencyOptions = (currencies ?? []).map((c) => ({
    value: c.code,
    label: `${c.code} - ${c.name}`,
  }));
  const creditControllerOptions = useMemo(() => {
    const options = (creditControllers ?? []).map((x) => ({
      value: x.id,
      label: x.name,
    }));
    const knownIds = new Set(options.map((x) => x.value));

    for (const id of draftFilters.creditControllerIds) {
      if (!knownIds.has(id)) {
        options.push({ value: id, label: `Credit Controller ${id}` });
      }
    }

    return options;
  }, [creditControllers, draftFilters.creditControllerIds]);

  const amountRangeInvalid = isRangeInvalid(draftFilters.amountMin, draftFilters.amountMax);
  const unallocatedRangeInvalid = isRangeInvalid(draftFilters.unallocatedMin, draftFilters.unallocatedMax);
  const hasInvalidRange = amountRangeInvalid || unallocatedRangeInvalid;

  const handleApply = () => {
    if (hasInvalidRange) return;

    if (draftPresetRef.current && filtersMatch(draftFilters, draftPresetRef.current.filters)) {
      onApplyPreset(draftPresetRef.current);
    } else {
      onApplyFilters(draftFilters);
    }
    draftPresetRef.current = null;
    onOpenChange(false);
  };

  const handlePresetClick = (preset: SavedFilterPreset<ReceiptFilters>) => {
    setDraftFilters({ ...preset.filters });
    draftPresetRef.current = preset;
  };

  const handleClearDraft = () => {
    setSelectedCustomers([]);
    draftPresetRef.current = null;
    setDraftFilters({
      customerIds: [],
      creditControllerIds: [],
      allocationStatuses: [],
      currencies: [],
      dateFrom: undefined,
      dateTo: undefined,
      amountMin: undefined,
      amountMax: undefined,
      unallocatedMin: undefined,
      unallocatedMax: undefined,
      hasPromise: undefined,
      onDisputeFilter: undefined,
      hasDispute: undefined,
      unappliedNotZero: undefined,
    });
  };

  // Determine which preset is visually "active" in the sheet:
  // If a preset was clicked (draft), use that; otherwise fall back to the applied activePresetId
  // but only if the draft filters still match that preset.
  const displayActivePresetId = useMemo(() => {
    if (draftPresetRef.current) {
      return filtersMatch(draftFilters, draftPresetRef.current.filters) ? draftPresetRef.current.id : null;
    }
    if (!activePresetId) return null;
    const appliedPreset = savedPresets.find((x) => x.id === activePresetId);
    if (!appliedPreset) return null;
    return filtersMatch(draftFilters, appliedPreset.filters) ? activePresetId : null;
  }, [draftFilters, activePresetId, savedPresets]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Discard draft changes when closing without applying
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col overflow-hidden">
        <SheetHeader className="px-6">
          <SheetTitle>Filter Receipts</SheetTitle>
          <SheetDescription>Apply filters to narrow down your receipt list</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto pr-4 pl-4 mt-6">
          <div className="space-y-6 pb-4">
            {/* Saved Presets */}
            {savedPresets.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Saved Presets</Label>
                <div className="space-y-2">
                  {savedPresets.map((preset) => (
                    <div
                      key={preset.id}
                      className="flex items-center gap-2 p-2 rounded-md border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <button
                        onClick={() => handlePresetClick(preset)}
                        className="flex items-center gap-2 flex-1 text-left"
                      >
                        {displayActivePresetId === preset.id ? (
                          <Star className="h-4 w-4 fill-primary text-primary" />
                        ) : (
                          <StarOff className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="text-sm font-medium">{preset.name}</span>
                      </button>
                      {!preset.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeletePreset(preset.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Separator />
              </div>
            )}

            {/* Customer Filter */}
            <div className="space-y-2">
              <Label>Customer</Label>
              <CustomerSearchMultiSelect
                selectedCustomers={selectedCustomers}
                onSelectionChange={handleCustomerSelectionChange}
                placeholder="Select customers..."
              />
            </div>

            {/* Credit Controller Filter */}
            <div className="space-y-2">
              <Label>Credit Controller</Label>
              <MultiSelect
                options={creditControllerOptions}
                placeholder="Select credit controllers..."
                emptyMessage="No assigned credit controllers found."
                value={draftFilters.creditControllerIds}
                onValueChange={(value) => setDraftFilters({ ...draftFilters, creditControllerIds: value })}
              />
            </div>

            {/* Allocation Status Filter */}
            <div className="space-y-2">
              <Label>Allocation Status</Label>
              <MultiSelect
                options={allocationStatuses}
                placeholder="Select allocation statuses..."
                emptyMessage="No allocation statuses found."
                value={draftFilters.allocationStatuses}
                onValueChange={(value) =>
                  setDraftFilters({
                    ...draftFilters,
                    allocationStatuses: value,
                    unappliedNotZero: value.length > 0 ? undefined : draftFilters.unappliedNotZero,
                  })
                }
              />
            </div>

            {/* Currency Filter */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <MultiSelect
                options={currencyOptions}
                placeholder="Select currencies..."
                emptyMessage="No currencies found."
                value={draftFilters.currencies}
                onValueChange={(value) => setDraftFilters({ ...draftFilters, currencies: value })}
              />
            </div>

            <Separator />

            {/* Date Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Date Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="receipt-date-from" className="text-sm">
                    From
                  </Label>
                  <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="receipt-date-from"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !draftFilters.dateFrom && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dateFrom ? formatDate(draftFilters.dateFrom) : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dateFrom ? parseLocalDate(draftFilters.dateFrom) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dateFrom: date ? toLocalDateString(date) : undefined,
                          });
                          setDateFromOpen(false);
                        }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="receipt-date-to" className="text-sm">
                    To
                  </Label>
                  <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="receipt-date-to"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !draftFilters.dateTo && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dateTo ? formatDate(draftFilters.dateTo) : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dateTo ? parseLocalDate(draftFilters.dateTo) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dateTo: date ? toLocalDateString(date) : undefined,
                          });
                          setDateToOpen(false);
                        }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <Separator />

            {/* Amount Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Amount (Gross) Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="receipt-amount-min" className="text-sm">
                    Min Amount
                  </Label>
                  <Input
                    id="receipt-amount-min"
                    type="number"
                    step="any"
                    placeholder="0"
                    aria-invalid={amountRangeInvalid}
                    value={draftFilters.amountMin ?? ''}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        amountMin: parseRangeInput(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="receipt-amount-max" className="text-sm">
                    Max Amount
                  </Label>
                  <Input
                    id="receipt-amount-max"
                    type="number"
                    step="any"
                    placeholder="No limit"
                    aria-invalid={amountRangeInvalid}
                    value={draftFilters.amountMax ?? ''}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        amountMax: parseRangeInput(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              {amountRangeInvalid && (
                <p role="alert" className="text-sm text-destructive">
                  {RANGE_MIN_GREATER_THAN_MAX_MESSAGE}
                </p>
              )}
            </div>

            <Separator />

            {/* Unallocated Amount Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Unallocated Amount Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="unallocated-min" className="text-sm">
                    Min Unallocated
                  </Label>
                  <Input
                    id="unallocated-min"
                    type="number"
                    step="any"
                    placeholder="0"
                    aria-invalid={unallocatedRangeInvalid}
                    value={draftFilters.unallocatedMin ?? ''}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        unallocatedMin: parseRangeInput(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unallocated-max" className="text-sm">
                    Max Unallocated
                  </Label>
                  <Input
                    id="unallocated-max"
                    type="number"
                    step="any"
                    placeholder="No limit"
                    aria-invalid={unallocatedRangeInvalid}
                    value={draftFilters.unallocatedMax ?? ''}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        unallocatedMax: parseRangeInput(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              {unallocatedRangeInvalid && (
                <p role="alert" className="text-sm text-destructive">
                  {RANGE_MIN_GREATER_THAN_MAX_MESSAGE}
                </p>
              )}
            </div>

            <Separator />

            {/* Flags Filters */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Flags</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="receipt-filter-external-dispute"
                    checked={draftFilters.onDisputeFilter ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        onDisputeFilter: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label
                    htmlFor="receipt-filter-external-dispute"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    External Dispute
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="receipt-filter-has-dispute"
                    checked={draftFilters.hasDispute ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        hasDispute: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label
                    htmlFor="receipt-filter-has-dispute"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Has Dispute
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="receipt-filter-has-promise"
                    checked={draftFilters.hasPromise ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        hasPromise: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label
                    htmlFor="receipt-filter-has-promise"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Has Promise to Pay
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <SheetFooter className="p-6 bg-background border-t">
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={handleClearDraft} className="flex-1">
              Clear All
            </Button>
            <Button
              variant="outline"
              onClick={() => onSavePreset(draftFilters)}
              disabled={hasInvalidRange}
              className="flex-1"
            >
              Save Preset
            </Button>
            <Button onClick={handleApply} disabled={hasInvalidRange} className="flex-1">
              Apply Filters
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
